<template>
    <a-modal
        id="slack-integrate-modal"
        :title="$t('integrations.slackIntegration')"
        wrapClassName="vertical-center-modal"
        :visible="modalVisible"
        :footer="null"
        @cancel="close()"
    >
        <div class="form-container">
            <div class="slack-description-box">
                <span class="slack-description-box__icon">
                    <i class="material-icons">info</i>
                </span>
                <div class="slack-description-box__content">
                    <div class="slack-description-box__text">
                        {{ $t('integrations.slackDescription') }}
                    </div>
                    <div class="slack-description-box__steps">
                        <strong>{{ $t('integrations.slackSetupTitle') }}</strong>
                        <ol>
                            <li>{{ $t('integrations.slackStep1') }}</li>
                            <li>{{ $t('integrations.slackStep2') }}</li>
                            <li>{{ $t('integrations.slackStep3') }}</li>
                            <li>{{ $t('integrations.slackStep4') }}</li>
                        </ol>
                    </div>
                </div>
            </div>

            <!-- Mevcut Kanallar -->
            <div v-if="slackChannels.length > 0" class="existing-channels mb-4">
                <div class="section-title">{{ $t('integrations.existingChannels') }}</div>
                <div v-for="channel in slackChannels" :key="channel._id" class="channel-item">
                    <div class="channel-info">
                        <span class="channel-name">{{ channel.name }}</span>
                        <a-tag :color="channel.enabled ? 'green' : 'red'">
                            {{ channel.enabled ? $t('common.active') : $t('common.inactive') }}
                        </a-tag>
                        <span v-if="channel.payload && channel.payload.defaultChannel" class="default-channel">
                            {{ channel.payload.defaultChannel }}
                        </span>
                    </div>
                    <div class="channel-actions">
                        <a-switch
                            :checked="channel.enabled"
                            @change="(checked) => toggleChannel(channel, checked)"
                        />
                        <a-button type="link" danger @click="deleteChannelConfirm(channel)">
                            <i class="material-icons">delete</i>
                        </a-button>
                    </div>
                </div>
            </div>

            <!-- Yeni Kanal Oluştur -->
            <div class="new-channel-form">
                <div class="section-title">{{ $t('integrations.createNewChannel') }}</div>
                
                <a-form :form="form" layout="vertical">
                    <a-form-item :label="$t('integrations.channelName')">
                        <a-input
                            v-decorator="['name', { rules: [{ required: true, message: $t('integrations.requiredField') }] }]"
                            :placeholder="$t('integrations.slackChannelNamePlaceholder')"
                        />
                    </a-form-item>

                    <a-form-item :label="$t('integrations.slackWebhookUrl')">
                        <a-input
                            v-decorator="['webhookUrl', { rules: [{ required: true, message: $t('integrations.requiredField') }, { pattern: /^https:\/\/hooks\.slack\.com\//, message: $t('integrations.slackWebhookUrlInvalid') }] }]"
                            :placeholder="$t('integrations.slackWebhookUrlPlaceholder')"
                        />
                        <div class="helper-text">
                            {{ $t('integrations.slackWebhookUrlHelper') }}
                        </div>
                    </a-form-item>

                    <a-form-item :label="$t('integrations.slackDefaultChannel')">
                        <a-input
                            v-decorator="['defaultChannel']"
                            :placeholder="$t('integrations.slackDefaultChannelPlaceholder')"
                        />
                        <div class="helper-text">
                            {{ $t('integrations.slackDefaultChannelHelper') }}
                        </div>
                    </a-form-item>
                </a-form>
            </div>
        </div>

        <div class="actions">
            <SupsisButton ghost @click="close">
                {{ $t('common.cancel') }}
            </SupsisButton>
            <SupsisButton primary :loading="loading" @click="createChannel">
                <i class="material-icons">add</i>
                {{ $t('integrations.createChannel') }}
            </SupsisButton>
        </div>
    </a-modal>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";
import { createChannel, updateChannel, deleteChannel } from "../../../../../../api/channel";

export default {
    name: "SlackIntegrationModal",
    props: {
        modalVisible: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        SupsisButton,
    },
    data() {
        return {
            loading: false,
            form: this.$form.createForm(this),
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels,
        }),
        slackChannels() {
            return this.channels.filter((channel) => channel.type === "slack" && !channel.isDeleted);
        },
    },
    methods: {
        ...mapActions({
            listChannels: "channel/listChannels",
        }),
        async createChannel() {
            this.form.validateFields(async (err, values) => {
                if (err) return;

                this.loading = true;
                try {
                    await createChannel(
                        "slack",
                        values.name,
                        {
                            webhookUrl: values.webhookUrl,
                            defaultChannel: values.defaultChannel || null,
                        }
                    );
                    this.$message.success(this.$t('integrations.channelCreated'));
                    this.form.resetFields();
                    await this.listChannels();
                } catch (error) {
                    console.error(error);
                    this.$message.error(this.$t('integrations.channelCreateError'));
                } finally {
                    this.loading = false;
                }
            });
        },
        async toggleChannel(channel, enabled) {
            try {
                await updateChannel(
                    channel._id,
                    channel.name,
                    enabled,
                    channel.payload,
                    channel.visibility
                );
                await this.listChannels();
                this.$message.success(enabled ? this.$t('integrations.channelActivated') : this.$t('integrations.channelDeactivated'));
            } catch (error) {
                console.error(error);
                this.$message.error(this.$t('integrations.channelUpdateError'));
            }
        },
        deleteChannelConfirm(channel) {
            this.$confirm({
                title: this.$t('integrations.deleteChannelConfirm'),
                content: this.$t('integrations.deleteChannelConfirmContent', { name: channel.name }),
                okText: this.$t('common.delete'),
                okType: 'danger',
                cancelText: this.$t('common.cancel'),
                onOk: async () => {
                    try {
                        await deleteChannel(channel._id);
                        await this.listChannels();
                        this.$message.success(this.$t('integrations.channelDeleted'));
                    } catch (error) {
                        console.error(error);
                        this.$message.error(this.$t('integrations.channelDeleteError'));
                    }
                },
            });
        },
        close() {
            this.$emit("close");
        },
    },
    mounted() {
        this.listChannels();
    },
};
</script>

<style lang="scss">
#slack-integrate-modal {
    .ant-modal-content {
        border-radius: 10px;
    }

    .ant-modal-header {
        border-radius: 10px 10px 0 0;
    }

    .ant-modal-body {
        border-radius: 0 0 10px 10px;
    }

    .form-container {
        .section-title {
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 12px;
            color: #333;
        }

        /* İzole bilgi kutusu: dışarıyı etkilemez, dışarıdan etkilenmez, uzun metin taşmaz */
        .slack-description-box {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 12px 16px;
            margin-bottom: 16px;
            background: #e6f7ff;
            border: 1px solid #91d5ff;
            border-radius: 6px;
            min-width: 0;
            max-width: 100%;
            box-sizing: border-box;

            &__icon {
                flex-shrink: 0;
                color: #1890ff;
                line-height: 1;

                .material-icons {
                    font-size: 20px;
                }
            }

            &__content {
                flex: 1;
                min-width: 0;
            }

            &__text {
                font-size: 14px;
                line-height: 1.5;
                color: rgba(0, 0, 0, 0.85);
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;
            }

            &__steps {
                margin-top: 8px;
                font-size: 14px;
                line-height: 1.5;
                color: rgba(0, 0, 0, 0.85);
                word-wrap: break-word;
                overflow-wrap: break-word;
                word-break: break-word;

                strong {
                    display: block;
                    margin-bottom: 4px;
                }

                ol {
                    margin: 0;
                    padding-left: 20px;
                }

                li {
                    margin-bottom: 2px;
                }
            }
        }

        .existing-channels {
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 12px;

            .channel-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px solid #f0f0f0;

                &:last-child {
                    border-bottom: none;
                }

                .channel-info {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .channel-name {
                        font-weight: 500;
                    }

                    .default-channel {
                        font-size: 12px;
                        color: #888;
                    }
                }

                .channel-actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            }
        }

        .new-channel-form {
            margin-top: 16px;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            padding: 12px;
        }

        .helper-text {
            font-size: 12px;
            color: #888;
            margin-top: 4px;
            min-width: 0;
            max-width: 100%;
            word-wrap: break-word;
            overflow-wrap: break-word;
            word-break: break-word;
        }
    }

    .actions {
        gap: 8px;
        display: flex;
        justify-content: flex-end;
        margin-top: 16px;
    }
}
</style>
